import { computed, ref } from "vue";

export interface VideoData {
	id: string;
	type: "youtube" | "youtube-shorts" | "tiktok" | "facebook" | "vimeo" | "unknown";
	url: string;
	thumbnailUrl: string;
}
/**
 * Video URL parser composable.
 * Supports YouTube, YouTube Shorts, TikTok, Facebook, and Vimeo.
 *
 * @param url - The video URL to parse.
 * @returns An object containing the parsed video data.
 */
export function useVideoParser(url: string) {
	const videoUrl = ref(url);

	const videoData = computed((): VideoData => {
		const urlString = videoUrl.value;

		// YouTube regular videos
		const youtubeMatch = urlString.match(
			/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
		);
		if (youtubeMatch) {
			const id = youtubeMatch[1];
			return {
				id,
				type: "youtube",
				url: urlString,
				thumbnailUrl: `https://img.youtube.com/vi/${id}/0.jpg`,
			};
		}

		// YouTube Shorts
		const youtubeShortsMatch = urlString.match(/(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([^"&?\/\s]{11})/);
		if (youtubeShortsMatch) {
			const id = youtubeShortsMatch[1];
			return {
				id,
				type: "youtube-shorts",
				url: urlString,
				thumbnailUrl: `https://img.youtube.com/vi/${id}/0.jpg`,
			};
		}

		// TikTok
		const tiktokMatch = urlString.match(/(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@[^\/]+\/video\/(\d+)/);
		if (tiktokMatch) {
			const id = tiktokMatch[1];
			return {
				id,
				type: "tiktok",
				url: urlString,
				thumbnailUrl: `https://www.tiktok.com/oembed?url=${encodeURIComponent(urlString)}`,
			};
		}

		// Facebook
		const facebookMatch = urlString.match(/(?:https?:\/\/)?(?:www\.)?facebook\.com\/.*\/videos\/(\d+)/);
		if (facebookMatch) {
			const id = facebookMatch[1];
			return {
				id,
				type: "facebook",
				url: urlString,
				thumbnailUrl: `https://graph.facebook.com/${id}/picture`,
			};
		}

		// Vimeo
		const vimeoMatch = urlString.match(/(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/);
		if (vimeoMatch) {
			const id = vimeoMatch[1];
			return {
				id,
				type: "vimeo",
				url: urlString,
				thumbnailUrl: `https://vumbnail.com/${id}.jpg`,
			};
		}

		return {
			id: "",
			type: "unknown",
			url: urlString,
			thumbnailUrl: "",
		};
	});

	return {
		videoData,
	};
}
